// src/components/Profile/ProfileInfo.js
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import { toast } from 'react-toastify';
import Loader from './Loader';
import ErrorMessage from './ErrorMessage';
import Footer from '../../Components/Footer/Footer';

const ProfileInfo = () => {
	const [userData, setUserData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [editMode, setEditMode] = useState(false);
	const [formData, setFormData] = useState({
		name: '',
		email: '',
		phone: ''
	});

	useEffect(() => {
		const fetchData = async () => {
			try {
				const token = Cookies.get("ecom_user_token");
				const response = await axios.get(
					`${process.env.REACT_APP_BACKEND_URL}/api/buyer/get-by-id`,
					{ headers: { Authorization: `Bearer ${token}` } }
				);
				setUserData(response?.data?.user);
				setFormData({
					name: response?.data?.user.name || '',
					email: response?.data?.user.email || '',
					phone: response?.data?.user.phone || ''
				});
			} catch (err) {
				setError(err.response?.data?.message || "Failed to fetch profile data");
				toast.error("Failed to load profile data");
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, []);

	const handleInputChange = (e) => {
		const { name, value } = e.target;
		setFormData(prev => ({
			...prev,
			[name]: value
		}));
	};

	const handleSubmit = async (e) => {
		e.preventDefault();
		try {
			const token = Cookies.get("ecom_user_token");
			await axios.patch(
				`${process.env.REACT_APP_BACKEND_URL}/api/buyer/update-profile`,
				formData,
				{ headers: { Authorization: `Bearer ${token}` } }
			);
			setUserData({ ...userData, ...formData });
			setEditMode(false);
			toast.success("Profile updated successfully!");
		} catch (err) {
			toast.error(err.response?.data?.message || "Failed to update profile");
		}
	};

	if (loading) return <Loader />;
	if (error) return <ErrorMessage message={error} />;
	if (!userData) return <ErrorMessage message="No user data available" />;

	return (
		<div
			className="bg-[#fdfaf6] rounded-2xl shadow-xl overflow-hidden p-8 border border-[#eaddcf] relative"
			style={{ backgroundImage: "url('https://www.transparenttextures.com/patterns/paper-fibers.png')" }}
		>
			{/* Decorative Corner Element */}
			<div className="absolute top-0 right-0 w-16 h-16 bg-[#efe0d0] opacity-20 rounded-bl-full -mr-8 -mt-8"></div>

			<div className="flex flex-col md:flex-row justify-between items-center mb-8 border-b-2 border-[#efe0d0] pb-4">
				<h2 className="text-xl md:text-2xl lg:text-3xl font-serif font-extrabold text-[#5d4037] tracking-tight">
					Profile Information
				</h2>
				{!editMode && (
					<button
						onClick={() => setEditMode(true)}
						className="px-6 py-2 mt-2 md:mt-0 bg-[#8d6e63] text-[#fdfaf6] font-serif font-bold rounded-full hover:bg-[#5d4037] transform hover:-translate-y-0.5 transition-all shadow-md active:shadow-inner"
					>
						Edit Profile
					</button>
				)}
			</div>

			{editMode ? (
				<form onSubmit={handleSubmit}>
					<div className="grid grid-cols-1 md:grid-cols-2 gap-8">
						<div className="group">
							<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-2 ml-1">Full Name</label>
							<input
								type="text"
								name="name"
								value={formData.name}
								onChange={handleInputChange}
								className="w-full p-4 bg-white/50 rounded-xl border-2 border-[#eaddcf] text-[#5d4037] font-serif focus:ring-0 focus:border-[#8d6e63] transition-all placeholder-[#d7ccc8]"
								required
							/>
						</div>

						<div>
							<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-2 ml-1">Email (Locked)</label>
							<input
								type="email"
								name="email"
								value={formData.email}
								onChange={handleInputChange}
								className="w-full p-4 bg-[#f5f5f5] rounded-xl border-2 border-gray-200 text-gray-400 font-serif cursor-not-allowed"
								required
								disabled
							/>
						</div>

						<div className="md:col-span-2">
							<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-2 ml-1">Phone Number</label>
							<input
								type="tel"
								name="phone"
								value={formData.phone}
								onChange={handleInputChange}
								className="w-full p-4 bg-white/50 rounded-xl border-2 border-[#eaddcf] text-[#5d4037] font-serif focus:ring-0 focus:border-[#8d6e63] transition-all"
							/>
						</div>

						<div className="md:col-span-2 flex justify-end space-x-4 mt-4">
							<button
								type="button"
								onClick={() => setEditMode(false)}
								className="px-6 py-2 text-[#8d6e63] font-serif font-bold hover:underline"
							>
								Discard
							</button>
							<button
								type="submit"
								className="px-8 py-3 bg-[#5d4037] text-[#fdfaf6] font-serif font-bold rounded-xl hover:bg-[#3e2723] transition-all shadow-lg"
							>
								Save Changes
							</button>
						</div>
					</div>
				</form>
			) : (
				<div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
					<div>
						<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-1 ml-1">Full Name</label>
						<div className="text-xl font-serif font-medium text-[#5d4037] border-b border-[#efe0d0] py-2">
							{userData.name}
						</div>
					</div>

					<div>
						<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-1 ml-1">Email Address</label>
						<div className="text-xl font-serif font-medium text-[#5d4037] border-b border-[#efe0d0] py-2">
							{userData.email}
						</div>
					</div>

					<div>
						<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-1 ml-1">Phone Number</label>
						<div className="text-xl font-serif font-medium text-[#5d4037] border-b border-[#efe0d0] py-2">
							{userData.phone || <span className="italic text-[#d7ccc8]">Not provided</span>}
						</div>
					</div>

					<div>
						<label className="block text-xs font-bold uppercase tracking-widest text-[#a1887f] mb-1 ml-1">Account Status</label>
						<div className="py-2 flex items-center">
							{userData.isActive ? (
								<span className="flex items-center text-[#5d4037] font-serif font-bold">
									<span className="w-2 h-2 bg-[#8bc34a] rounded-full mr-2 shadow-[0_0_8px_rgba(139,195,74,0.6)]"></span>
									Verified
								</span>
							) : (
								<span className="text-red-400 font-serif italic">Inactive</span>
							)}
						</div>
					</div>

					<div className="md:col-span-2 grid grid-cols-2 gap-8 pt-6 opacity-60">
						{userData.createdAt && (
							<div>
								<label className="block text-[10px] font-bold uppercase tracking-widest text-[#a1887f]">Registered</label>
								<div className="text-sm font-serif text-[#5d4037]">
									{new Date(userData.createdAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
								</div>
							</div>
						)}

						{userData.updatedAt && (
							<div className="text-right">
								<label className="block text-[10px] font-bold uppercase tracking-widest text-[#a1887f]">Last Entry</label>
								<div className="text-sm font-serif text-[#5d4037]">
									{new Date(userData.updatedAt).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
								</div>
							</div>
						)}
					</div>
				</div>
			)}
		</div>

	);
};

export default ProfileInfo;